import type {
  ReservationSession,
  ReservationSessionItem,
  ReservationSessionProduct,
} from "../types/session";
import type { SEMItemConfig, SEMProductConfig } from "../types/flow";

const AFFILIATION = "Online Store";
const CURRENCY = "EUR";

export interface GtmItem {
  item_id: string;
  item_name: string;
  affiliation: string;
  price: number;
  quantity: number;
}

interface DataLayerWindow extends Window {
  dataLayer?: Record<string, unknown>[];
}

function push(
  event: string,
  items: GtmItem[],
  extra: Record<string, unknown> = {},
) {
  const w = window as DataLayerWindow;
  if (!w.dataLayer) w.dataLayer = [];
  // Reset previous ecommerce object before pushing a new one.
  w.dataLayer.push({ ecommerce: null });
  w.dataLayer.push({
    event,
    ecommerce: {
      currency: CURRENCY,
      ...extra,
      items,
    },
  });
}

export function itemConfigToGtmItem(
  item: SEMItemConfig,
  price: number,
  quantity = 1,
): GtmItem {
  return {
    item_id: item.id,
    item_name: item.name,
    affiliation: AFFILIATION,
    price,
    quantity,
  };
}

export function productConfigToGtmItem(
  product: SEMProductConfig,
  quantity: number,
): GtmItem {
  return {
    item_id: product.id,
    item_name: product.name,
    affiliation: AFFILIATION,
    price: product.pricePerUnitIn,
    quantity,
  };
}

export function sessionItemToGtmItem(item: ReservationSessionItem): GtmItem {
  return {
    item_id: item.semItemConfig.id,
    item_name: item.semItemConfig.name,
    affiliation: AFFILIATION,
    price: item.pricePerUnitIn,
    quantity: item.facilitiesNeeded || 1,
  };
}

export function sessionProductToGtmItem(
  product: ReservationSessionProduct,
  personCount: number,
): GtmItem {
  const multiplier =
    product.semProductConfig.type === "perPersonTotal"
      ? product.quantity * personCount
      : product.quantity;
  return {
    item_id: product.semProductConfig.id,
    item_name: product.semProductConfig.name,
    affiliation: AFFILIATION,
    price: product.pricePerUnitIn,
    quantity: multiplier,
  };
}

export function sessionToGtmItems(session: ReservationSession): GtmItem[] {
  return [
    ...session.items.map(sessionItemToGtmItem),
    ...session.products.map((p) =>
      sessionProductToGtmItem(p, session.personCount),
    ),
  ];
}

export function trackViewItem(items: GtmItem[]) {
  if (items.length === 0) return;
  push("view_item", items);
}

export function trackAddToCart(items: GtmItem[], value?: number) {
  push("add_to_cart", items, value != null ? { value } : {});
}

export function trackRemoveFromCart(items: GtmItem[], value?: number) {
  push("remove_from_cart", items, value != null ? { value } : {});
}

export function trackViewCart(session: ReservationSession) {
  push("view_cart", sessionToGtmItems(session), {
    value: session.totalAfterDiscount ?? session.total,
  });
}

export function trackBeginCheckout(session: ReservationSession) {
  push("begin_checkout", sessionToGtmItems(session), {
    value: session.totalAfterDiscount ?? session.total,
  });
}

export function trackPurchase(session: ReservationSession) {
  push("purchase", sessionToGtmItems(session), {
    transaction_id: session.id,
    value: session.totalAfterDiscount ?? session.total,
  });
}
